import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api';

export interface AdminSession {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  permissions: string[];
  mustChangePassword: boolean;
  /** Whether this account already has an authenticator set up. */
  twoFactorEnabled: boolean;
  /** The studio demands one from this role and this account has none yet. */
  twoFactorRequired: boolean;
}

export function useSession() {
  return useQuery<AdminSession | null>({
    queryKey: ['session'],
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        return await api<AdminSession>('/admin/me');
      } catch (error) {
        if (error instanceof ApiError && error.code === 'PASSWORD_CHANGE_REQUIRED') {
          throw error;
        }
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string; code?: string }) =>
      api<AdminSession>('/admin/auth/login', { method: 'POST', body: input }),
    onSuccess: (session) => {
      queryClient.setQueryData(['session'], session);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: true }>('/admin/auth/logout', { method: 'POST' }),
    onSuccess: () => {
      queryClient.setQueryData(['session'], null);
      queryClient.clear();
    },
  });
}

export function useChangePassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api<{ ok: true }>('/admin/auth/change-password', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['session'] }),
  });
}

export const can = (session: AdminSession | null | undefined, permission: string): boolean =>
  Boolean(session?.permissions.includes(permission));

/** Setting up the authenticator: a secret to scan, then a code to prove it. */
export function useStartTwoFactor() {
  return useMutation({
    mutationFn: () => api<{ secret: string; uri: string }>('/admin/auth/2fa/start', { method: 'POST' }),
  });
}

export function useEnableTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      api<{ enabled: true }>('/admin/auth/2fa/enable', { method: 'POST', body: { code } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['session'] }),
  });
}

export function useDisableTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (password: string) =>
      api<{ enabled: false }>('/admin/auth/2fa/disable', { method: 'POST', body: { password } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['session'] }),
  });
}
